import type { ValueOf } from 'type-fest'

import type { GradeElementaryMiddle, GradeHigh } from '~/@entities/SchoolSystem'

import type { CurriculumType, TrieKey } from './schemas'

const ELEMENTARY_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_초등학교_1학년_1학기: '1.2.5.12.50',
  CURRICULUM_KEY_초등학교_1학년_2학기: '1.2.5.12.68',
  CURRICULUM_KEY_초등학교_2학년_1학기: '1.2.5.14.52',
  CURRICULUM_KEY_초등학교_2학년_2학기: '1.2.5.14.70',

  CURRICULUM_KEY_초등학교_1학년_1학기_22개정: '1.4.4145.4148.4157',
  CURRICULUM_KEY_초등학교_1학년_2학기_22개정: '1.4.4145.4148.4158',
  CURRICULUM_KEY_초등학교_2학년_1학기_22개정: '1.4.4145.4149.4159',
  CURRICULUM_KEY_초등학교_2학년_2학기_22개정: '1.4.4145.4149.4160',
  CURRICULUM_KEY_초등학교_5학년_2학기_22개정: '1.4.4145.4152.4166',
  CURRICULUM_KEY_초등학교_6학년_2학기_22개정: '1.4.4145.4153.4168',
} satisfies Record<string, TrieKey>

const MIDDLE_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_중학교_1학년: '1.2.9.27',
  CURRICULUM_KEY_중학교_2학년: '1.2.9.29',
  CURRICULUM_KEY_중학교_3학년: '1.2.9.31',
  CURRICULUM_KEY_중학교_1학년_22개정: '1.4.4146.4154',
  CURRICULUM_KEY_중학교_2학년_22개정: '1.4.4146.4155',
  CURRICULUM_KEY_중학교_1학년_1학기_22개정: '1.4.4146.4154.4169',
  CURRICULUM_KEY_중학교_1학년_2학기_22개정: '1.4.4146.4154.4170',

  CURRICULUM_KEY_중학교_1학년_2학기: '1.2.9.27.80',
  CURRICULUM_KEY_중학교_2학년_1학기_22개정: '1.4.4146.4155.4171',
  CURRICULUM_KEY_중학교_2학년_2학기_22개정: '1.4.4146.4155.4172',
  CURRICULUM_KEY_중학교_3학년_1학기_22개정: '1.4.4146.4156.4173',
  CURRICULUM_KEY_중학교_3학년_2학기_22개정: '1.4.4146.4156.4174',
} satisfies Record<string, TrieKey>

const HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_수학_상: '1.2.7.41',
  CURRICULUM_KEY_고등학교_수학_하: '1.2.7.42',
  CURRICULUM_KEY_고등학교_수학I: '1.2.7.43',
  CURRICULUM_KEY_고등학교_수학II: '1.2.7.44',
  CURRICULUM_KEY_고등학교_확률과통계: '1.2.7.45',
  CURRICULUM_KEY_고등학교_미적분: '1.2.7.46',
  CURRICULUM_KEY_고등학교_기하: '1.2.7.47',

  CURRICULUM_KEY_고등학교_공통수학1_22개정: '1.4.4147.4175',
  CURRICULUM_KEY_고등학교_공통수학2_22개정: '1.4.4147.4176',
  CURRICULUM_KEY_고등학교_대수_22개정: '1.4.4147.4177',
  CURRICULUM_KEY_고등학교_미적분1_22개정: '1.4.4147.4178',
  CURRICULUM_KEY_고등학교_확률과통계_22개정: '1.4.4147.4179',
  CURRICULUM_KEY_고등학교_미적분2_22개정: '1.4.4147.4180',
  CURRICULUM_KEY_고등학교_기하_22개정: '1.4.4147.4181',
} satisfies Record<string, TrieKey>

export const NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS = {
  CURRICULUM_KEY_고등학교_교육과정_외: '1.2.7.48',
  CURRICULUM_KEY_고등학교_구_교육과정: '1.2.7.49',
  CURRICULUM_KEY_고등학교_구_교육과정_22개정: '1.4.4147.4182',
  CURRICULUM_KEY_고등학교_교육과정_외_22개정: '1.4.4147.4183',
} satisfies Record<string, TrieKey>

export const CURRICULUM_KEYS = {
  ...ELEMENTARY_SCHOOL_CURRICULUM_KEYS,
  ...MIDDLE_SCHOOL_CURRICULUM_KEYS,
  ...HIGH_SCHOOL_CURRICULUM_KEYS,
} satisfies Record<string, TrieKey>

// 1차 오픈 키(= 사전 오픈 = 풀오픈 이전)
export const PRE_OPEN_CURRICULUM_KEYS: readonly TrieKey[] = [
  MIDDLE_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_중학교_3학년_2학기_22개정,
]

/**
 * 연산 문항이 없는 과목 → 연산 축으로 바꿀 때 대신 고를 과목.
 * 사전 오픈(PRE_OPEN) 해제와 연산 문항 준비는 별개 시점이라 파생하지 않고 명시한다
 */
export const ARITHMETIC_UNSUPPORTED_CURRICULUM_FALLBACK = {
  [MIDDLE_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_중학교_3학년_2학기_22개정]:
    MIDDLE_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_중학교_3학년_1학기_22개정,
} satisfies Partial<Record<TrieKey, TrieKey>>

export const getArithmeticFallbackCurriculumKey = (trieKey: TrieKey) =>
  ARITHMETIC_UNSUPPORTED_CURRICULUM_FALLBACK[trieKey]

export const isArithmeticSupportedCurriculumKey = (trieKey: TrieKey) =>
  getArithmeticFallbackCurriculumKey(trieKey) === undefined

/** 학년이 없는 고등 과목을 학년으로 분류한 값. 표에 없는 과목은 3학년이다 */
export const HIGH_SCHOOL_CATEGORIZED_GRADE: Partial<Record<TrieKey, GradeElementaryMiddle>> = {
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학1_22개정]: '1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학2_22개정]: '1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상]: '1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하]: '1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학I]: '2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학II]: '2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정]: '2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정]: '2',
}

export const DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY: TrieKey =
  HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상

const SUBJECT_HIGH_KO = {
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상]: '고등수학(상)',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하]: '고등수학(하)',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학I]: '수학 I',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학II]: '수학 II',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계]: '확률과 통계',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분]: '미적분',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하]: '기하',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학1_22개정]: '공통수학1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학2_22개정]: '공통수학2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정]: '대수',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정]: '미적분1',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정]: '확률과 통계',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분2_22개정]: '미적분2',
  [HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정]: '기하',
} as const satisfies Record<
  ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>,
  Exclude<GradeHigh, '(구) 교육과정' | '교육과정 외'>
>

const NON_STANDARD_SUBJECT_HIGH_KO = {
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_교육과정_외]: '교육과정 외',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_구_교육과정]: '(구) 교육과정',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_구_교육과정_22개정]:
    '(구) 교육과정',
  [NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_교육과정_외_22개정]:
    '교육과정 외',
} as const satisfies Record<
  ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>,
  Extract<GradeHigh, '(구) 교육과정' | '교육과정 외'>
>

/** 고등 과목 trieKey → 과목명 */
export const SUBJECT_HIGH_KO_BY_TRIE_KEY = {
  ...SUBJECT_HIGH_KO,
  ...NON_STANDARD_SUBJECT_HIGH_KO,
} as const satisfies Record<
  | ValueOf<typeof HIGH_SCHOOL_CURRICULUM_KEYS>
  | ValueOf<typeof NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS>,
  GradeHigh
>

/** 단원 트리 노드의 깊이(depth). 학기·과목이 0 이다 */
export const CURRICULUM_DEPTH = {
  SEMESTER: 0,
  SUBJECT: 0,
  BIG_CHAPTER: 1,
  MIDDLE_CHAPTER: 2,
  LITTLE_CHAPTER: 3,
  CONCEPT: 4,
  TOPIC: 5,
  SUB_TOPIC: 6,
} as const satisfies Partial<Record<CurriculumType, number>>
